import React from 'react';
import { Form } from 'react-bootstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';

const FormFieldRenderer = ({ field, value, onChange, isInvalid, isFullWidth = false }) => {
  // Helper function to check if field should be full width
  const shouldRenderFullWidth = () => {
    if (isFullWidth) return true;
    
    const lowerFieldName = field.name.toLowerCase().replace(/[_\s]/g, '');
    return lowerFieldName === 'context' || 
           lowerFieldName === 'maintask' || 
           lowerFieldName === 'main_task';
  };

  const fullWidthStyle = shouldRenderFullWidth() ? { 
    width: '100%', 
    maxWidth: 'none', 
    boxSizing: 'border-box' 
  } : {};

  // Handle array fields with object items
  if (field.type === 'array' && field.items?.type === 'object') {
    const arrayValue = Array.isArray(value) ? value : [];
    
    const handleAddItem = () => {
      const newItem = {};
      // Initialize with default values based on schema
      if (field.items.properties) {
        Object.keys(field.items.properties).forEach(key => {
          const prop = field.items.properties[key];
          if (prop.type === 'string') {
            newItem[key] = prop.default || '';
          } else if (prop.type === 'number' || prop.type === 'integer') {
            newItem[key] = prop.default || 0;
          } else if (prop.type === 'boolean') {
            newItem[key] = prop.default || false;
          } else if (prop.type === 'array') {
            newItem[key] = [];
          } else if (prop.type === 'object') {
            newItem[key] = {};
          }
        });
      }
      onChange(field.name, [...arrayValue, newItem]);
    };

    const handleRemoveItem = (index) => {
      const newArray = arrayValue.filter((_, i) => i !== index);
      onChange(field.name, newArray);
    };

    const handleItemChange = (index, itemField, itemValue) => {
      const newArray = [...arrayValue];
      newArray[index] = {
        ...newArray[index],
        [itemField]: itemValue
      };
      onChange(field.name, newArray);
    };

    return (
      <div className="array-field-container" style={fullWidthStyle}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted small fw-medium">
            {arrayValue.length} item{arrayValue.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleAddItem}
          >
            <FaPlus className="me-1" />
            Add Item
          </button>
        </div>

        {arrayValue.map((item, index) => (
          <div key={index} className="array-item-card mb-3 p-3 border rounded bg-dark">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <strong className="text-primary">Item {index + 1}</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleRemoveItem(index)}
              >
                <FaTrash />
              </button>
            </div>

            {field.items.properties && Object.keys(field.items.properties).map((propKey) => {
              const propSchema = field.items.properties[propKey];
              const isRequired = field.items.required?.includes(propKey) || false;
              const propValue = item[propKey] !== undefined ? item[propKey] : '';

              return (
                <Form.Group key={propKey} className="mb-3">
                  <Form.Label className={`form-field-label ${isRequired ? 'required-field' : ''}`} style={{ paddingLeft: '12px' }}>
                    {propSchema.title || propKey}
                  </Form.Label>
                  
                  {propSchema.enum ? (
                    <Form.Select
                      value={propValue}
                      onChange={(e) => handleItemChange(index, propKey, e.target.value)}
                      isInvalid={isRequired && !propValue}
                      className="form-field-control"
                    >
                      <option value="">Select...</option>
                      {propSchema.enum.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                  ) : propSchema.type === 'boolean' ? (
                    <Form.Check
                      type="checkbox"
                      checked={!!propValue}
                      onChange={(e) => handleItemChange(index, propKey, e.target.checked)}
                      className="form-field-check"
                      style={{ paddingLeft: '12px' }}
                    />
                  ) : propSchema.type === 'number' || propSchema.type === 'integer' ? (
                    <Form.Control
                      type="number"
                      value={propValue}
                      onChange={(e) => handleItemChange(index, propKey, e.target.value)}
                      isInvalid={isRequired && !propValue}
                      className="form-field-control"
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      value={propValue}
                      onChange={(e) => handleItemChange(index, propKey, e.target.value)}
                      placeholder={propSchema.description || ''}
                      isInvalid={isRequired && !propValue}
                      className="form-field-control"
                    />
                  )}
                  
                  {propSchema.description && (
                    <Form.Text className="text-muted d-block mt-1 form-field-help" style={{ paddingLeft: '12px' }}>
                      {propSchema.description}
                    </Form.Text>
                  )}
                </Form.Group>
              );
            })}
          </div>
        ))}

        {arrayValue.length === 0 && (
          <div className="text-center text-muted py-4 empty-state">
            <div className="mb-2">📝</div>
            <div>No items added yet. Click "Add Item" to add the first item.</div>
          </div>
        )}
      </div>
    );
  }

  // Handle array fields with primitive items
  if (field.type === 'array') {
    const arrayValue = Array.isArray(value) ? value : [];
    
    const handleAddItem = () => {
      onChange(field.name, [...arrayValue, '']);
    };

    const handleRemoveItem = (index) => {
      const newArray = arrayValue.filter((_, i) => i !== index);
      onChange(field.name, newArray);
    };

    const handleItemChange = (index, newValue) => {
      const newArray = [...arrayValue];
      newArray[index] = newValue;
      onChange(field.name, newArray);
    };

    return (
      <div className="array-field-container" style={fullWidthStyle}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted small fw-medium">
            {arrayValue.length} item{arrayValue.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleAddItem}
          >
            <FaPlus className="me-1" />
            Add Item
          </button>
        </div>

        {arrayValue.map((item, index) => (
          <div key={index} className="d-flex gap-2 mb-2 array-item-row">
            <Form.Control
              type="text"
              value={item}
              onChange={(e) => handleItemChange(index, e.target.value)}
              placeholder={`Item ${index + 1}`}
              isInvalid={isInvalid}
              className="form-field-control flex-grow-1"
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-danger flex-shrink-0"
              onClick={() => handleRemoveItem(index)}
            >
              <FaTrash />
            </button>
          </div>
        ))}

        {arrayValue.length === 0 && (
          <div className="text-center text-muted py-3 empty-state">
            <div className="mb-2">📋</div>
            <div>No items added yet. Click "Add Item" to add the first item.</div>
          </div>
        )}
      </div>
    );
  }

  // Handle enum fields (dropdown)
  if (field.enum || field.options) {
    const options = field.enum || field.options;
    return (
      <Form.Select
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        isInvalid={isInvalid}
        style={fullWidthStyle}
        className="form-field-control"
      >
        <option value="">Select...</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Form.Select>
    );
  }

  // Handle boolean fields
  if (field.type === 'boolean') {
    return (
      <Form.Check
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(field.name, e.target.checked)}
        isInvalid={isInvalid}
        className="form-field-check"
        style={{ paddingLeft: '12px' }}
      />
    );
  }

  // Handle number fields
  if (field.type === 'number' || field.type === 'integer') {
    return (
      <Form.Control
        type="number"
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        isInvalid={isInvalid}
        style={fullWidthStyle}
        className="form-field-control"
      />
    );
  }

  // Handle textarea fields - including Context and main_task
  if (field.format === 'textarea' || 
      field.name.toLowerCase().includes('instructions') ||
      field.name.toLowerCase().includes('architecture') ||
      field.name.toLowerCase().includes('description') ||
      shouldRenderFullWidth()) {
    return (
      <Form.Control
        as="textarea"
        rows={6}
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.description || ''}
        isInvalid={isInvalid}
        style={{ ...fullWidthStyle, boxSizing: 'border-box' }}
        className="form-field-control form-field-textarea"
      />
    );
  }

  // Default: text input
  return (
    <Form.Control
      type="text"
      value={value || ''}
      onChange={(e) => onChange(field.name, e.target.value)}
      placeholder={field.description || ''}
      isInvalid={isInvalid}
      style={fullWidthStyle}
      className="form-field-control"
    />
  );
};

export default FormFieldRenderer;